import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { EventsManager, ReelsEvents, WinEvents } from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { TimelineLabels } from "../../../abstracts/types/enums";
import { Apocalypse } from "../../Apocalypse";
import { winLevels } from "../../config/ApocalypseConfig";
import ApocalypseReel from "../../modules/Reels/normal/ApocalypseReel";
import { ApocalypseWinView } from "../../modules/Winnings/ApocalypseWinView";
import { totalWinDecorator } from "../decorators/ApocalypseTotalWinDecorators";

export class ApocalypseNormalWinPresentation extends AbstractFlowPresentation {
    protected ucReels: ApocalypseReel[];
    protected winView: ApocalypseWinView;
    protected noWinCounter: number = 0;

    constructor(name: string = `ApocalypseNormalWinPresentation`) {
        super(name);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (winView: ApocalypseWinView) => {
            this.winView = winView;
        });

        EventsManager.on(ReelsEvents.GET_NORMAL_REELS, (reels: AbstractReel[]) => {
            this.ucReels = reels as ApocalypseReel[];
        });
    }

    protected shouldFinishPresentation(): boolean {
        return this.model.gameData.accumulatedRoundWin <= 0;
    }

    public start(flowStateController: FlowStateController) {
        super.start(flowStateController);

        if (this.shouldFinishPresentation()) {
            this.addSpecialAnimations();

            return;
        }

        this.handleWinnings();
    }

    protected addSpecialAnimations() {
        super.addSpecialAnimations();

        this.finishPresentation();
    }

    protected handleWinnings() {
        const isWinBelowStake: boolean = Apocalypse.model.isWinBelowStake();

        totalWinDecorator({
            winLevels: winLevels,
            winAmount: this.model.gameData.accumulatedRoundWin,
            timeline: this.flowPresentationTimeline,
            winScreen: this.winView.totalWinScreen,
            betValue: this.model.gameData.playedBetValue,
            flowStateController: this.controller,
            winIsBelowStake: isWinBelowStake,
            reels: this.ucReels,
            winSymbolsPos: Apocalypse.model.finalWinningPosition,
            betWayWins: this.model.gameData.betWayWins,
            currentGameMode: this.model.gameData.currentMode,
            nextGameMode: this.model.gameData.nextMode,
            keypadPlugin: this.keypad,
            finishSpineAnimationDuration: 5,
        });

        if (this.model.data.wasFeatureBuy || isWinBelowStake) {
            SlotGame.keypadPlugin.setWin(
                this.model.gameData.totalSpinWinnings,
                false,
                isWinBelowStake,
                true
            );
            this.flowPresentationTimeline.resume();
            return;
        }

        this.flowPresentationTimeline.call(
            () => {
                SlotGame.keypadPlugin.setWin(
                    this.model.gameData.accumulatedRoundWin,
                    false,
                    isWinBelowStake,
                    false
                );
            },
            [],
            TimelineLabels.TEXT_COUNT_COMPLETE
        );

        this.flowPresentationTimeline.resume();

        this.noWinCounter = 0;
    }

    protected finishPresentation() {
        if (
            this.model.gameData.mode === AbstractGameMode.NORMAL &&
            (this.model.gameData.nextMode === AbstractGameMode.FREESPIN ||
                this.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN ||
                this.model.gameData.nextMode === AbstractGameMode.FREESPIN_BETTER)
        ) {
            this.controller?.setSkipCallback(undefined);
            this.winView.showFreespinIntroScreen(
                () => {
                    super.finishPresentation();
                },
                this.model.data.freespinsLeft,
                this.model.gameData.nextMode
            );

            return;
        }

        const delay =
            this.model.gameData.nextMode !== AbstractGameMode.NORMAL &&
            this.model.gameData.nextMode !== this.model.gameData.mode
                ? 0.4
                : 0;

        gsap.delayedCall(delay, () => {
            super.finishPresentation();
        });
    }
}
